<template>
  <Head
    :title="
      t('page.title', {
        clientId: ltiRegistration.clientId,
      })
    "
  />
  <s-page-block size="md" class="flex-1">
    <s-page-header class="mb-5" :heading="t('page.title')" :overline="ltiRegistration.clientId">
      <template #actions>
        <s-btn icon="plus" @click.prevent="open = true">
          {{ t('actions.add') }}
        </s-btn>
      </template>
    </s-page-header>

    <s-data-table
      :data="ltiDeployments"
      :columns="[
        {key: 'lmsIssuedDeploymentId', label: t('table.identifier')},
        {key: 'createdByEmail', label: t('table.createdBy')},
        {
          key: 'createdAt',
          label: t('table.createdAt'),
        },
        {
          key: 'actions',
          label: '',
          sortable: false,
        },
      ]"
    >
      <template #td:createdAt="deployment">
        {{ monthDayYear(deployment.createdAt.date) }} {{ timeOfDay(deployment.createdAt.date) }}
      </template>
      <template #td:actions="deployment">
        <s-btn
          size="sm"
          color="red-light"
          icon="trash-can"
          method="delete"
          :aria-label="t('ariaLabel.delete', {deployment: deployment.id})"
          :href="
            route('institutions.lti-registrations.lti-deployments.destroy', {
              institution: institution.id,
              ltiRegistration: ltiRegistration.id,
              ltiDeployment: deployment.id,
            })
          "
          class="w-fit"
        >
          {{ t('actions.delete') }}
        </s-btn>
      </template>
    </s-data-table>
  </s-page-block>
  <s-modal
    v-model:open="open"
    :title="t('titles.modal')"
    @confirm="
      form.post(
        route('institutions.lti-registrations.lti-deployments.store', {
          institution: institution.id,
          ltiRegistration: ltiRegistration.id,
        }),
        {
          onSuccess() {
            form.reset();
          },
        }
      )
    "
    :confirm="{label: t('form.save'), icon: 'content-save'}"
  >
    <template #content>
      <s-input-field
        id="deploymentId"
        v-model="form.lmsIssuedDeploymentId"
        :label="t('labels.lmsIssuedDeploymentId')"
        :errors="form.errors.lmsIssuedDeploymentId"
      />
    </template>
  </s-modal>
</template>
<script setup lang="ts">
import {useI18n} from 'vue-i18n';
import SPageBlock from '../../design-system/SPageBlock.vue';
import SBtn from '../../design-system/SBtn.vue';
import SFormInline from '../../design-system/SFormInline.vue';
import {Head, useForm} from '@inertiajs/vue3';
import {route} from 'ziggy-js';
import {Institution} from '../../types/entities/institution';
import {monthDayYear, timeOfDay} from '../../format/dates';
import SPageHeader from '../../design-system/SPageHeader.vue';
import {LaravelDate} from '../../types/laravel-date';
import SDataTable from '../../design-system/SDataTable.vue';
import SModal from '../../design-system/SModal.vue';
import {ref} from 'vue';
import SInputField from '../../design-system/SInputField.vue';

defineProps<{
  institution: Institution;
  ltiRegistration: {id: number; clientId: string};
  ltiDeployments: {
    id: number;
    lmsIssuedDeploymentId: number;
    createdAt: LaravelDate;
    createdByEmail: string;
  }[];
}>();

const {t} = useI18n();

const open = ref(false);

const form = useForm({
  lmsIssuedDeploymentId: '',
});
</script>
<i18n>
{
  "en": {
    "page.title": "LTI Deployments",
    "labels.lmsIssuedDeploymentId": "Enter the Deployment ID from your LMS",
    "titles.modal": "Add LTI Deployment",
    "table": {
      "identifier": "Deployment ID",
      "createdBy": "Created By",
      "createdAt": "Created At"
    },
    "ariaLabel.delete": "Delete LTI Deployment: {deployment}."
  },
  "fr": {
    "page.title": "Déploiements  LTI",
    "labels.lmsIssuedDeploymentId": "Entrez l'ID de déploiement de votre LMS",
    "titles.modal": "Ajouter un déploiement LTI",
    "table": {
      "identifier": "ID de déploiement",
      "createdBy": "Créé par",
      "createdAt": "Créé à"
    },
    "ariaLabel.delete": "Supprimer le déploiement LTI : {deployment}."
  }
}
</i18n>
